import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  fetchReportOutstandingBalances,
  type OutstandingBalancesQuery,
} from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import {
  localDateStr,
  useReportFilters,
  useReportQueryParams,
} from '@/pages/reports/report-filters-context.js';

const AGE_BUCKETS = ['all', 'current', '1-30', '31-60', '61-90', '90+'] as const;

function lastMonthRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastPrev = new Date(firstThis.getTime() - 86400000);
  const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
  return { dateFrom: localDateStr(firstPrev), dateTo: localDateStr(lastPrev) };
}

export function OutstandingBalancesReportPage() {
  const base = useReportQueryParams();
  const filters = useReportFilters();
  const [recordType, setRecordType] = useState<'all' | 'tickets' | 'sales'>('all');
  const [customer, setCustomer] = useState('');
  const [minBalance, setMinBalance] = useState('');
  const [ageBucket, setAgeBucket] =
    useState<(typeof AGE_BUCKETS)[number]>('all');
  const [customerDraft, setCustomerDraft] = useState('');
  const [minDraft, setMinDraft] = useState('');

  const qParams: OutstandingBalancesQuery = useMemo(() => {
    const q: OutstandingBalancesQuery = {
      ...base,
      recordType,
      ageBucket,
    };
    if (customer.trim()) q.customer = customer.trim();
    if (minBalance.trim()) q.minBalance = Number(minBalance);
    return q;
  }, [base, recordType, customer, minBalance, ageBucket]);

  const q = useQuery({
    queryKey: ['reports-outstanding-balances', qParams],
    queryFn: () => fetchReportOutstandingBalances(qParams),
  });

  const d = q.data;

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.rows.map((r) => ({
        number: r.ticketNumber,
        type: r.recordType,
        customer: r.customerName,
        phone: r.phone ?? '',
        created: r.createdAt.slice(0, 10),
        ageDays: r.ageDays,
        total: r.total,
        amountPaid: r.amountPaid,
        balanceDue: r.balanceDue,
        status: r.status,
        assignedTech: r.assignedTech ?? '',
      })),
      'outstanding-balances.csv',
    );
  };

  if (q.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading outstanding…</div>;
  }
  if (q.isError || !d) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load outstanding balances report.
      </div>
    );
  }

  const s = d.summary;
  const agingKeys = ['current', '1-30', '31-60', '61-90', '90+'] as const;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Outstanding balances</h2>
          <p className="text-sm text-slate-500">
            Accounts receivable — tickets and sales with a remaining balance. Uses the same balance as
            checkout.
          </p>
        </div>
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
          onClick={csv}
        >
          Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
          onClick={() => {
            const r = lastMonthRange();
            filters.setPeriod('custom');
            filters.setDateFrom(r.dateFrom);
            filters.setDateTo(r.dateTo);
          }}
        >
          Last month
        </button>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-600">Type</span>
          <select
            className="rounded-lg border border-slate-300 px-2 py-1.5"
            value={recordType}
            onChange={(e) => setRecordType(e.target.value as typeof recordType)}
          >
            <option value="all">All</option>
            <option value="tickets">Tickets only</option>
            <option value="sales">Sales only</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-600">Age</span>
          <select
            className="rounded-lg border border-slate-300 px-2 py-1.5"
            value={ageBucket}
            onChange={(e) => setAgeBucket(e.target.value as typeof ageBucket)}
          >
            {AGE_BUCKETS.map((b) => (
              <option key={b} value={b}>
                {b === 'all' ? 'All ages' : b}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-600">Customer</span>
          <input
            className="rounded-lg border border-slate-300 px-2 py-1.5"
            placeholder="Name or phone"
            value={customerDraft}
            onChange={(e) => setCustomerDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setCustomer(customerDraft);
            }}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-600">Min balance</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="w-28 rounded-lg border border-slate-300 px-2 py-1.5"
            value={minDraft}
            onChange={(e) => setMinDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setMinBalance(minDraft);
            }}
          />
        </label>
        <button
          type="button"
          className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white"
          onClick={() => {
            setCustomer(customerDraft);
            setMinBalance(minDraft);
          }}
        >
          Apply
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Total outstanding</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{formatCurrency(s.totalOutstanding)}</p>
          <p className="text-sm text-slate-600">{s.count} unpaid records</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Tickets</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{formatCurrency(s.ticketsOutstanding)}</p>
          <p className="text-sm text-slate-600">{s.ticketsCount} records</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Sales</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{formatCurrency(s.salesOutstanding)}</p>
          <p className="text-sm text-slate-600">{s.salesCount} records</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-xs font-medium text-slate-500">Aging</p>
          <ul className="space-y-1 text-sm">
            {agingKeys.map((k) => (
              <li key={k} className="flex justify-between gap-2">
                <span className="text-slate-600">{k}</span>
                <span className="font-semibold tabular-nums">
                  {formatCurrency(s.aging[k].total)}{' '}
                  <span className="font-normal text-slate-400">({s.aging[k].count})</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Age</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Balance due</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Tech</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                  No outstanding balances in this range.
                </td>
              </tr>
            ) : (
              d.rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 font-semibold">
                    <Link className="text-primary hover:underline" to={`/tickets/${r.id}`}>
                      {r.ticketNumber}
                    </Link>
                    <span className="ml-1 text-xs font-normal text-slate-400">{r.recordType}</span>
                  </td>
                  <td className="px-3 py-2">
                    {r.customerId ? (
                      <Link className="font-medium text-primary hover:underline" to={`/customers/${r.customerId}`}>
                        {r.customerName}
                      </Link>
                    ) : (
                      <span className="font-medium">{r.customerName}</span>
                    )}
                    {r.phone ? <div className="text-xs text-slate-500">{r.phone}</div> : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                  <td className="px-3 py-2">
                    {r.ageDays}d
                    <span className="ml-1 text-xs text-slate-400">{r.ageBucket}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.amountPaid)}</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-amber-800">
                    {formatCurrency(r.balanceDue)}
                  </td>
                  <td className="px-3 py-2">{prettyLabel(r.status)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.assignedTech ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
